import { InfiniteData } from '@tanstack/react-query';

import { MessagingChannel, MessagingMessage } from '~hooks/useMessaging/models';
import { MessagingChannelsPage } from '~queries/useMessagingChannelsInfiniteQuery';

const isOlderThanPreview = (channel: MessagingChannel, message: MessagingMessage) =>
  channel.lastMessage !== null &&
  message.createdAt.isBefore(channel.lastMessage.createdAt);

/**
 * The api orders conversations by most recent message, so this is what a re-read would return,
 * minus the request. Returns the list untouched when the row already heads it showing this
 * message or already shows newer activity, and `null` when no loaded page lists it — heading the
 * list with a row it has never held would draw a conversation with no data.
 */
export const moveChannelToTop = (
  channelList: InfiniteData<MessagingChannelsPage>,
  channelId: string,
  message: MessagingMessage,
) => {
  let listedChannel: MessagingChannel | undefined;

  const pagesWithoutChannel = channelList.pages.map((page) => {
    const channelIndex = page.channels.findIndex((channel) => channel.id === channelId);
    if (channelIndex === -1) return page;

    listedChannel = page.channels[channelIndex];

    return {
      ...page,
      channels: page.channels.filter((_, index) => index !== channelIndex),
    };
  });

  if (!listedChannel) return null;
  if (isOlderThanPreview(listedChannel, message)) return channelList;

  const isAlreadyOnTop = channelList.pages[0]?.channels[0] === listedChannel;
  const isAlreadyPreviewed = listedChannel.lastMessage?.id === message.id;
  if (isAlreadyOnTop && isAlreadyPreviewed) return channelList;

  const [firstPage, ...remainingPages] = pagesWithoutChannel;

  return {
    ...channelList,
    pages: [
      {
        ...firstPage,
        channels: [listedChannel.withLastMessage(message), ...firstPage.channels],
      },
      ...remainingPages,
    ],
  };
};

const mapMatchingChannels = (
  channelList: InfiniteData<MessagingChannelsPage>,
  isMatch: (channel: MessagingChannel) => boolean,
  transform: (channel: MessagingChannel) => MessagingChannel,
) => {
  let isAnyMatched = false;

  const pages = channelList.pages.map((page) => {
    if (!page.channels.some(isMatch)) return page;

    isAnyMatched = true;

    return {
      ...page,
      channels: page.channels.map((channel) =>
        isMatch(channel) ? transform(channel) : channel,
      ),
    };
  });

  return isAnyMatched ? { ...channelList, pages } : null;
};

/**
 * An edit or delete keeps the row where it is and only changes its preview, and only when the
 * changed message is the one it previews. Returns `null` when no loaded row previews it.
 */
export const replaceChannelPreview = (
  channelList: InfiniteData<MessagingChannelsPage>,
  channelId: string,
  message: MessagingMessage,
) =>
  mapMatchingChannels(
    channelList,
    (channel) => channel.id === channelId && channel.lastMessage?.id === message.id,
    (channel) => channel.withLastMessage(message),
  );

export const patchListedChannel = (
  channelList: InfiniteData<MessagingChannelsPage>,
  channelId: string,
  patch: (channel: MessagingChannel) => MessagingChannel,
) => mapMatchingChannels(channelList, (channel) => channel.id === channelId, patch);

export const removeListedChannel = (
  channelList: InfiniteData<MessagingChannelsPage>,
  channelId: string,
) => ({
  ...channelList,
  pages: channelList.pages.map((page) => ({
    ...page,
    channels: page.channels.filter((channel) => channel.id !== channelId),
  })),
});
